import { Link } from 'react-router';
import type { NotificationCategory } from '@/features/communications/centerTypes';
import styles from '@/features/communications/center.module.css';
import type { UnreadCounts } from './api';

/** Unread counts per category in the bell panel; each links to the filtered Notification Center. */
export function BellSummary({
  counts,
  categories,
  onNavigate,
}: {
  counts: UnreadCounts | null | undefined;
  categories: NotificationCategory[];
  onNavigate: () => void;
}) {
  const entries = Object.entries(counts?.byCategory ?? {}).filter(([, n]) => n > 0);
  if (entries.length === 0) return null;
  const label = (code: string) => categories.find((c) => c.code === code)?.label ?? code;
  const order = (code: string) => {
    const i = categories.findIndex((c) => c.code === code);
    return i === -1 ? 99 : i;
  };
  return (
    <nav className={styles.bellSummary} aria-label="Unread by category">
      {entries
        .sort(([a], [b]) => order(a) - order(b))
        .map(([code, n]) => (
          <Link
            key={code}
            to={`/notifications?category=${encodeURIComponent(code)}`}
            className={styles.bellCategory}
            onClick={onNavigate}
            data-testid={`bell-category-${code}`}
          >
            {label(code)} <strong>{n > 99 ? '99+' : n}</strong>
            <span className="sr-only"> unread</span>
          </Link>
        ))}
    </nav>
  );
}

/** "View all" + "Preferences" links at the bottom of the bell panel. */
export function BellFooter({ onNavigate }: { onNavigate: () => void }) {
  return (
    <div className={styles.bellFooter}>
      <Link to="/notifications" onClick={onNavigate}>
        View all notifications
      </Link>
      <Link to="/account/notifications" onClick={onNavigate}>
        Preferences
      </Link>
    </div>
  );
}
